/**
 * 抽壳功能控制器（主线程编排中枢）。
 *
 * 设计说明：
 * 1. 本控制器只负责“进入/退出抽壳、吸收默认选择、预览与提交编排”；
 * 2. 鼠标事件与外观沿用 Viewer 默认实体选择链路，不自定义拾取与叠加层；
 * 3. 真正的几何求解由 Viewer.commitFeature / Viewer.executeFeaturePreview 走 worker 完成，
 *    主线程禁止直接 import shell-kernel 或 getPuppyKernel。
 */

import type * as THREE from 'three/webgpu';
import type { Subscription } from 'rxjs';
import type { Viewer } from '../../viewer';
import {
    EntityInteractionEventType,
    type SelectionUpdatedEventPayload,
} from '../../manager/EntitySelectionManager';
import { convertMmToM, convertMToMm } from '../../utils/unit';
import { resolveTopologySelectionPick } from '../../utils/feature';
import { buildShellModelTopologyBindings } from '../binding/buildShellModelTopologyBindings';
import { ShellFeatureDefinitionModel } from '../definition/shell/ShellFeatureDefinition';

/** 默认抽壳壁厚（mm）。 */
const DEFAULT_SHELL_THICKNESS_MM = 2;

/**
 * 抽壳功能控制器。
 */
export class ShellFeatureController {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** 选择事件订阅集合。 */
    private readonly subscriptions: Subscription[] = [];
    /** 状态变化监听器集合（供宿主响应式刷新）。 */
    private readonly stateListeners = new Set<() => void>();
    /** 是否处于抽壳交互态。 */
    private active = false;
    /** 编辑态特征 ID；新建为 null。 */
    private editingFeatureId: string | null = null;
    /** 壁厚（底层米制）。 */
    private thickness = convertMmToM(DEFAULT_SHELL_THICKNESS_MM);
    /** 壁厚方向。 */
    private direction: ShellThicknessDirection = 'inward';
    /** 已吸收的移除面选择项。 */
    private removedFaces: SelectionItemRef[] = [];
    /** 是否整体内部掏空（保留所有外表面）。 */
    private hollowWholeBody = false;
    /** 整体掏空模式下选中的上游实体对象 ID。 */
    private selectedBodyObjectId: string | null = null;
    /** 抑制选择管理器同步回放的选择事件。 */
    private suppressSelectionReplay = false;
    /** 最近一次抽壳预览失败的用户提示；成功或清除后为 null。 */
    private previewError: string | null = null;
    /** 最近一次预览失败对应的输入签名，用于避免相同输入反复重试。 */
    private lastPreviewFailureKey: string | null = null;
    /** 进入抽壳前的选择模式。 */
    private selectionModeBeforeShell: 'object' | 'face' | 'edge' | 'vertex' | 'face-edge' | 'topology' = 'object';

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 是否处于抽壳交互态。
     *
     * @returns 交互态。
     */
    public get isActive(): boolean {
        return this.active;
    }

    /**
     * 订阅控制器状态变化。
     *
     * @param listener 状态变化监听器。
     * @returns 取消订阅函数。
     */
    public subscribe(listener: () => void): () => void {
        this.stateListeners.add(listener);
        return () => {
            this.stateListeners.delete(listener);
        };
    }

    /**
     * 通知宿主控制器状态已变化。
     */
    private notifyStateChanged(): void {
        for (const listener of this.stateListeners) {
            listener();
        }
    }

    /**
     * 进入抽壳功能。
     *
     * 设计说明：
     * 1. 抽壳只吸收面，进入时必须把底层拾取约束为 face 模式，否则默认对象模式选不到 FeatureFace；
     * 2. 保留进入前的选择模式，退出时恢复。
     */
    public enterShellFeature(): void {
        if (this.active) {
            return;
        }
        this.active = true;
        this.editingFeatureId = null;
        this.removedFaces = [];
        this.selectedBodyObjectId = null;
        this.previewError = null;
        this.lastPreviewFailureKey = null;
        this.selectionModeBeforeShell = this.viewer.getSelectionMode();
        // 整体掏空模式用 object 选择（点模型任意处即选中整个实体并高亮）；普通模式用 face 选择。
        this.viewer.setSelectionMode(this.hollowWholeBody ? 'object' : 'face', { preserveSelection: true });
        this.bindSelectionEvents();
        this.notifyStateChanged();
    }

    /**
     * 退出抽壳功能。
     */
    public exitShellFeature(): void {
        if (!this.active) {
            return;
        }
        this.active = false;
        this.unbindSelectionEvents();
        this.removedFaces = [];
        this.selectedBodyObjectId = null;
        this.previewError = null;
        this.lastPreviewFailureKey = null;
        this.viewer.setSelectionMode(this.selectionModeBeforeShell, { preserveSelection: true });
        this.viewer.clearFeaturePreviewRequests();
        void this.viewer.setExtrudePreviewMesh(null);
        this.notifyStateChanged();
    }

    /**
     * 同步当前选中的面到移除面列表。
     *
     * @returns 当前移除面列表快照。
     */
    public getSelectedFaces(): SelectionItemRef[] {
        return this.removedFaces.map((item) => ({ ...item }));
    }

    /**
     * 获取当前壁厚（mm）。
     *
     * @returns 壁厚（mm）。
     */
    public getThicknessMm(): number {
        return convertMToMm(this.thickness);
    }

    /**
     * 获取当前壁厚方向。
     *
     * @returns 壁厚方向。
     */
    public getDirection(): ShellThicknessDirection {
        return this.direction;
    }

    /**
     * 获取是否整体内部掏空模式。
     *
     * @returns 整体掏空标记。
     */
    public get isHollowWholeBody(): boolean {
        return this.hollowWholeBody;
    }

    /**
     * 设置整体内部掏空模式。
     *
     * @param checked 是否启用整体掏空。
     */
    public setHollowWholeBody(checked: boolean): void {
        this.hollowWholeBody = checked;
        this.removedFaces = [];
        this.selectedBodyObjectId = null;
        if (this.active) {
            this.viewer.setSelectionMode(checked ? 'object' : 'face', { preserveSelection: true });
        }
        this.notifyStateChanged();
        void this.previewShell();
    }

    /**
     * 判断当前抽壳是否允许提交。
     *
     * @returns 可提交标记。
     */
    public getShellCanSubmit(): boolean {
        if (!this.active || this.thickness <= 0) {
            return false;
        }
        if (this.hollowWholeBody) {
            return typeof this.selectedBodyObjectId === 'string' && this.selectedBodyObjectId.length > 0;
        }
        return this.removedFaces.length > 0;
    }

    /**
     * 获取最近一次抽壳预览失败的用户提示。
     *
     * @returns 预览失败提示；无失败时为 null。
     */
    public getShellPreviewError(): string | null {
        return this.previewError;
    }

    /**
     * 清除抽壳预览失败提示（由 UI 在弹窗确认后调用）。
     */
    public clearShellPreviewError(): void {
        if (this.previewError === null) {
            return;
        }
        this.previewError = null;
        this.notifyStateChanged();
    }

    /**
     * 获取面向属性面板的抽壳定义投影。
     *
     * @returns 抽壳定义快照。
     */
    public getShellDefinition(): ShellFeatureDefinition {
        return {
            removedFaces: this.removedFaces.map((item) => ({ ...item })),
            thickness: this.thickness,
            direction: this.direction,
            tolerance: 1e-6,
            hollowWholeBody: this.hollowWholeBody,
        };
    }

    /**
     * 设置壁厚（mm）并触发预览。
     *
     * @param mm 壁厚（mm）。
     */
    public setThicknessMm(mm: number): void {
        if (!Number.isFinite(mm) || mm <= 0) {
            return;
        }
        this.thickness = convertMmToM(mm);
        this.notifyStateChanged();
        void this.previewShell();
    }

    /**
     * 设置壁厚方向并触发预览。
     *
     * @param direction 壁厚方向。
     */
    public setDirection(direction: ShellThicknessDirection): void {
        this.direction = direction;
        this.notifyStateChanged();
        void this.previewShell();
    }

    /**
     * 预览抽壳。
     */
    public async previewShell(): Promise<void> {
        if (!this.active) {
            return;
        }
        if (this.hollowWholeBody) {
            if (!this.selectedBodyObjectId) {
                await this.viewer.setExtrudePreviewMesh(null);
                return;
            }
        } else if (this.removedFaces.length === 0) {
            // 没有移除面时清除上一次的残留预览，避免取消最后一面后预览体不消失。
            await this.viewer.setExtrudePreviewMesh(null);
            return;
        }

        const inputKey = this.buildPreviewInputKey();
        // 同一输入上一次已求解失败时跳过，避免用户每点击一次都重复触发失败与提示。
        if (this.lastPreviewFailureKey === inputKey) {
            return;
        }

        const { definitionModel, bindings } = this.buildShellInputs();
        try {
            const result = await this.viewer.executeFeaturePreview({
                featureKind: 'shell',
                featureId: this.editingFeatureId,
                definition: definitionModel,
                bindings,
            });
            // executeFeaturePreview 只返回预览数据，不负责显示；必须显式把 mesh 挂到预览层。
            if (result.meshData) {
                await this.viewer.setExtrudePreviewMesh(result.meshData as never, {
                    prototype: result.prototype,
                    draftDefinition: result.draftDefinition,
                });
            }
            // 预览成功后清除失败签名与失败提示。
            this.lastPreviewFailureKey = null;
            if (this.previewError !== null) {
                this.previewError = null;
                this.notifyStateChanged();
            }
        } catch (error) {
            // 复杂切除体等几何可能求解失败；捕获后转为用户提示，避免 uncaught rejection 打断抽壳交互态。
            await this.viewer.setExtrudePreviewMesh(null);
            this.lastPreviewFailureKey = inputKey;
            this.previewError = '当前选中实体/面不支持抽壳';
            // 自动取消当前选中面/实体，用户可以直接选择其他面重试，无需先手动取消。
            this.clearFailedPreviewSelection();
            this.notifyStateChanged();
        }
    }

    /**
     * 构建当前抽壳预览输入的签名。
     *
     * 设计说明：
     * 1. 签名覆盖移除面/实体选择、壁厚与方向，用于判断两次预览请求是否等价；
     * 2. 复杂体求解失败后，相同签名直接跳过，避免选择事件高频重试同一失败输入；
     * 3. 任一输入变化都会改变签名，从而允许用户换面/改参数后重新尝试求解。
     *
     * @returns 输入签名。
     */
    private buildPreviewInputKey(): string {
        const baseKey = `${this.thickness}:${this.direction}`;
        if (this.hollowWholeBody) {
            return `hollow:${this.selectedBodyObjectId ?? ''}:${baseKey}`;
        }
        const faceRefs = this.removedFaces
            .map((item) => item.persistentRef)
            .sort()
            .join('|');
        return `faces:${faceRefs}:${baseKey}`;
    }

    /**
     * 获取面向宿主的抽壳提交载荷。
     *
     * @returns 提交载荷（featureId/定义模型/绑定/上游实体 ID）。
     */
    public getShellCommitPayload(): {
        featureId: string | null;
        definitionModel: ShellFeatureDefinitionModel;
        bindings: ReturnType<typeof buildShellModelTopologyBindings>;
        upstreamBodyObjectId: string | null;
    } {
        const { definitionModel, bindings } = this.buildShellInputs();
        return {
            featureId: this.editingFeatureId,
            definitionModel,
            bindings,
            upstreamBodyObjectId: this.hollowWholeBody
                ? this.selectedBodyObjectId
                : this.removedFaces[0]?.bodyObjectId ?? null,
        };
    }

    /**
     * 执行抽壳（✔ 提交）。
     */
    public async executeShell(): Promise<void> {
        if (!this.active) {
            return;
        }
        if (this.removedFaces.length === 0) {
            throw new Error('抽壳至少需要选择一个移除面');
        }
        const upstreamBodyObjectId = this.removedFaces[0]?.bodyObjectId ?? null;
        const { definitionModel, bindings } = this.buildShellInputs();
        const result = await this.viewer.commitFeature({
            featureKind: 'shell',
            featureId: this.editingFeatureId,
            definition: definitionModel,
            bindings,
        });

        // 新建抽壳成功后，把正式结果体网格替换到场景：新增结果体、移除旧实体与残留预览。
        if (!result.replaced && result.meshData) {
            await this.viewer.addResultMesh(result.meshData as never, {
                prototype: result.prototype,
            });
            if (upstreamBodyObjectId && result.objectId && upstreamBodyObjectId !== result.objectId) {
                this.viewer.removeResultMesh(upstreamBodyObjectId);
                this.viewer.removeSceneObject(upstreamBodyObjectId);
            }
        }
        await this.viewer.setExtrudePreviewMesh(null);

        this.exitShellFeature();
    }

    /**
     * 绑定默认选择事件。
     */
    private bindSelectionEvents(): void {
        if (this.subscriptions.length > 0) {
            return;
        }
        const selectionManager = this.viewer.entitySelectionManager;
        if (!selectionManager) {
            return;
        }
        this.subscriptions.push(
            selectionManager
                .observe(EntityInteractionEventType.SelectionUpdated)
                .subscribe(this.handleSelectionUpdated),
        );
    }

    /**
     * 解绑选择事件。
     */
    private unbindSelectionEvents(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /**
     * 选择变化回调：面模式按点击切换移除面，整体模式同步选中实体。
     */
    private readonly handleSelectionUpdated = (payload: SelectionUpdatedEventPayload): void => {
        if (!this.active || this.suppressSelectionReplay) {
            return;
        }
        if (this.hollowWholeBody) {
            this.syncSelectedBody();
        } else {
            this.applyFaceSelectionToggle(payload);
        }
        this.notifyStateChanged();
        void this.previewShell();
    };

    /**
     * 面模式点击切换：再次点击已选面即取消选择。
     */
    private applyFaceSelectionToggle(payload: SelectionUpdatedEventPayload): void {
        const clickedObjects = payload.selectionSourceObjects ?? payload.selectedObjects ?? [];
        if (clickedObjects.length === 0) {
            return;
        }
        const toggled = new Map(this.removedFaces.map((item) => [item.persistentRef, item]));
        for (const object of clickedObjects) {
            const pick = resolveTopologySelectionPick(object);
            if (!pick || pick.kind !== 'face') {
                continue;
            }
            if (toggled.has(pick.persistentRef)) {
                toggled.delete(pick.persistentRef);
            } else {
                toggled.set(pick.persistentRef, {
                    kind: 'face',
                    bodyObjectId: pick.bodyObjectId,
                    subEntityId: pick.subEntityId,
                    persistentRef: pick.persistentRef,
                    owningFeatureId: pick.owningFeatureId,
                    runtimeTopologyHint: pick.runtimeTopologyHint,
                });
            }
        }
        this.removedFaces = Array.from(toggled.values());
        this.syncManagerSelectionToRemovedFaces();
    }

    /**
     * 把选择管理器的实际选中集同步为当前移除面列表（取消点击的面）。
     */
    private syncManagerSelectionToRemovedFaces(): void {
        const manager = this.viewer.entitySelectionManager;
        if (!manager) {
            return;
        }
        const keptRefs = new Set(this.removedFaces.map((item) => item.persistentRef));
        const keptPicks = manager.getSelectedPicks().filter((pick) => {
            const ref = resolveTopologySelectionPick(pick.object)?.persistentRef;
            return ref ? keptRefs.has(ref) : false;
        });
        this.suppressSelectionReplay = true;
        try {
            manager.updateSelectionState(keptPicks);
        } finally {
            queueMicrotask(() => {
                this.suppressSelectionReplay = false;
            });
        }
    }

    /**
     * 预览失败时自动取消当前抽壳选择。
     *
     * 设计说明：
     * 1. 失败的面/实体不支持抽壳，保留选中会诱导用户反复点击同一目标；
     * 2. 直接清空移除面与实体选择并同步到选择管理器，用户可立即点选其他目标重试。
     */
    private clearFailedPreviewSelection(): void {
        this.removedFaces = [];
        this.selectedBodyObjectId = null;
        const manager = this.viewer.entitySelectionManager;
        if (!manager) {
            return;
        }
        this.suppressSelectionReplay = true;
        try {
            manager.updateSelectionState([]);
        } finally {
            queueMicrotask(() => {
                this.suppressSelectionReplay = false;
            });
        }
    }

    /**
     * 整体模式：把当前默认选中的对象投影为上游实体。
     */
    private syncSelectedBody(): void {
        const objects = this.viewer.entitySelectionManager?.getSelectedObjects() ?? [];
        // 场景中的实体对象在运行时挂载 CAD objectId；沿用内核既有的 Object3D 交叉断言读取，
        // 避免把 Object3D 断言成无关联的裸对象结构（那会被 TS 判定为不重叠转换）。
        const selectedBody = objects.find((object) => (
            typeof (object as THREE.Object3D & { objectId?: string }).objectId === 'string'
        ));
        this.selectedBodyObjectId = selectedBody
            ? (selectedBody as THREE.Object3D & { objectId: string }).objectId
            : null;
        this.removedFaces = [];
    }

    /**
     * 组装抽壳定义模型与持久化绑定。
     *
     * @returns 定义模型与绑定。
     */
    private buildShellInputs(): {
        definitionModel: ShellFeatureDefinitionModel;
        bindings: ReturnType<typeof buildShellModelTopologyBindings>;
    } {
        const definition: ShellFeatureDefinition = {
            removedFaces: this.removedFaces.map((item) => ({ ...item })),
            thickness: this.thickness,
            direction: this.direction,
            tolerance: 1e-6,
            hollowWholeBody: this.hollowWholeBody,
        };
        const upstreamBodyObjectId = this.hollowWholeBody
            ? this.selectedBodyObjectId
            : this.removedFaces[0]?.bodyObjectId ?? null;
        const definitionModel = ShellFeatureDefinitionModel.fromParams({
            commandParams: {
                featureKind: 'shell',
                upstreamBodyObjectId: upstreamBodyObjectId ?? undefined,
                definition,
            },
        });
        const bindings = buildShellModelTopologyBindings({
            upstreamBodyObjectId,
            definition,
        });
        return { definitionModel, bindings };
    }
}
